<template>
  <v-container fluid class="home-page pa-0" fill-height>
    <v-layout>
      <v-flex>
        <div class="home-inner">
          <div class="home-groups-grid">
            <v-card
              v-for="group in allowedGroups"
              :key="group.key"
              class="home-group-card"
              flat
            >
              <v-card-title
                v-if="group.title"
                class="primary--text home-group-title py-4 py-md-5"
              >
                {{ group.title }}
              </v-card-title>
              <v-card-text
                class="pb-4 pb-md-6"
                :class="[
                  group.title ? 'pt-0' : 'pt-4 pt-md-5',
                  { 'home-group-text--split-halves': group.key === 'candidates-manual' },
                ]"
              >
                <div
                  class="home-items-grid"
                  :class="{ 'home-items-grid--split-halves': group.key === 'candidates-manual' }"
                >
                  <v-card
                    v-for="item in group.items"
                    :key="item.key"
                    class="pa-3 pa-sm-4 home-subcard d-flex flex-column"
                    :class="{ 'home-subcard--split-halves': group.key === 'candidates-manual' }"
                    flat
                    outlined
                    @click="openItem(item)"
                  >
                    <h6 class="mb-2 primary--text home-subcard-title">{{ item.title }}</h6>
                    <p class="mb-0 caption grey--text flex-grow-1 home-subcard-description">
                      {{ item.description }}
                    </p>
                  </v-card>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import homeNavigation from '@/config/homeNavigation.json';
import utils from '@/utils/utils';
import featureFlags from '@/config/featureFlags';

// Home nav cards gated by a feature flag. Keys match homeNavigation.json items.
const FEATURE_FLAG_BY_NAV_KEY = {
  'next-semantica': 'manualSemantica',
  'analysis-queue': 'analysisQueue',
  'analysis-groups': 'analysisGroups',
  'analysis-runs': 'folderRuns',
};

export default {
  name: 'Home',
  components: {},
  data() {
    return {
      utils: utils,
      homeGroups: homeNavigation.groups || [],
      notAllowed: [],
    };
  },
  computed: {
    allowedGroups() {
      return this.homeGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => this.checkIsAllowed(item.permissionId) && this.isFeatureEnabled(item)
          ),
        }))
        .filter((g) => g.items.length > 0);
    },
  },
  methods: {
    checkIsAllowed(id) {
      return utils.config.preProd || !this.notAllowed.includes(id);
    },
    isFeatureEnabled(item) {
      const flagKey = FEATURE_FLAG_BY_NAV_KEY[item.key];
      return flagKey ? !!featureFlags[flagKey] : true;
    },
    openItem(item) {
      if (!item?.route) return;
      if (!this.checkIsAllowed(item.permissionId) || !this.isFeatureEnabled(item)) return;
      this.$router.push(item.route);
    },
  },
};
</script>

<style scoped>
/* Horizontal padding + top offset below app bar */
.home-page {
  padding-top: 56px;
}

@media (min-width: 960px) {
  .home-page {
    padding-top: 72px;
  }
}

.home-inner {
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 12px;
  padding-right: 12px;
  padding-bottom: 24px;
}

@media (min-width: 600px) {
  .home-inner {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media (min-width: 1264px) {
  .home-inner {
    padding-left: 24px;
    padding-right: 24px;
  }
}

/* Group blocks: one column on phone/tablet, two columns on desktop */
.home-groups-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: stretch;
}

@media (min-width: 1264px) {
  .home-groups-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
}

.home-group-card {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: none !important;
  min-width: 0;
  height: 100%;
}

.home-group-title {
  font-size: 1.25rem;
  line-height: 1.3;
  word-break: break-word;
}

@media (min-width: 600px) {
  .home-group-title {
    font-size: 1.5rem;
  }
}

@media (min-width: 960px) {
  .home-group-title {
    font-size: 1.75rem;
  }
}

/* Subcards: fluid columns from min card width */
.home-items-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

@media (min-width: 480px) {
  .home-items-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .home-items-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  .home-items-grid--split-halves {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, minmax(0, 1fr));
    height: 100%;
  }
}

@media (min-width: 1264px) {
  .home-items-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 10px;
  }

  .home-items-grid--split-halves {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, minmax(0, 1fr));
    height: 100%;
  }
}

.home-subcard {
  min-height: 96px;
  cursor: pointer;
  box-shadow: none !important;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.home-subcard-title {
  font-size: 1rem;
  line-height: 1.35;
  word-break: break-word;
}

@media (min-width: 600px) {
  .home-subcard {
    min-height: 100px;
  }
}

.home-subcard:hover {
  border-color: rgba(0, 0, 0, 0.18) !important;
  background-color: rgba(0, 0, 0, 0.02);
}

/* Candidates + NEXT Semantica in Home: larger cards with centered content. */
.home-subcard--split-halves {
  min-height: 0;
  height: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.home-subcard--split-halves .home-subcard-title {
  width: 100%;
  margin-bottom: 8px !important;
}

.home-subcard--split-halves .home-subcard-description {
  width: 100%;
  flex-grow: 0 !important;
}

.home-group-text--split-halves {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
</style>
